import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import Seo from '@/components/Seo';
import { AUTH } from '@/constants/testIds';

export default function RegisterPage() {
  const { register, error } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await register(form.name, form.email, form.password);
    setBusy(false);
    if (ok) nav(next && next.startsWith('/') ? next : '/workspace');
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <Seo title="Create account" noindex />
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 tm-tag-stripe opacity-10" />
        <div className="relative">
          <span className="font-display font-semibold tracking-tight text-2xl text-slate-900">Trades<em className="italic text-brand">Mate</em></span>
          <h2 className="mt-12 font-display font-semibold text-5xl uppercase tracking-tight leading-[0.95]">Get every calc.<br/><span className="text-brand">In your pocket.</span></h2>
          <p className="mt-6 text-slate-300 max-w-md">Free account. Save quotes. Use the AI estimator on tough jobs.</p>
        </div>
        <p className="relative text-xs uppercase tracking-widest text-slate-400 font-semibold">© TradesMate UK</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="tm-card p-8 w-full max-w-md space-y-5">
          <h1 className="font-display font-semibold text-3xl uppercase tracking-tight">Create account</h1>
          <p className="text-sm text-slate-600 -mt-3">It&apos;s free. Always.</p>
          <div>
            <label className="tm-label">Name</label>
            <input data-testid={AUTH.registerName} required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="tm-input" placeholder="Dave the Builder"/>
          </div>
          <div>
            <label className="tm-label">Email</label>
            <input data-testid={AUTH.registerEmail} type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="tm-input" placeholder="you@trade.co.uk"/>
          </div>
          <div>
            <label className="tm-label">Password (min 6)</label>
            <input data-testid={AUTH.registerPassword} type="password" required minLength={6} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="tm-input" placeholder="••••••••"/>
          </div>
          {error && <p className="text-sm font-mono text-red-600 bg-red-50 border border-red-600 p-2">{error}</p>}
          <button data-testid={AUTH.registerSubmit} disabled={busy} className="tm-btn w-full">
            {busy ? 'Creating…' : 'Create account'}
          </button>
          <p className="text-[11px] font-mono text-slate-500 leading-relaxed">
            By creating an account you agree to our{' '}
            <Link to="/legal/terms" className="underline hover:text-brand">Terms of Service</Link>{' '}and{' '}
            <Link to="/legal/privacy" className="underline hover:text-brand">Privacy Policy</Link>. Calculator and AI outputs are estimates only.
          </p>
          <p className="text-sm text-slate-600">
            Already have one?{' '}
            <Link to="/login" data-testid={AUTH.switchToLogin} className="underline underline-offset-4 font-bold text-slate-900 hover:text-brand">Log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
